<template>
  <div
    class="_rich-tooltip-wrapper"
    @mouseleave="onMouseLeave">
    <div
      ref="activator"
      class="_tooltip-activator"
      @mouseenter="target ? null : onMouseEnter"
      @mouseleave="target ? null : onActivatorMouseLeave"
      @click="target ? null : onClick"
    >
      <slot
        name="activator"
        v-bind="{
          on: target ? {} : { mouseenter: onMouseEnter, mouseleave: onActivatorMouseLeave, click: onClick },
          attrs: { 'aria-describedby': tooltipId }
        }"
      >
        <v-btn
          icon
          small
          :color="activatorColor"
        >
          <v-icon :size="iconSize">
            {{ activatorIcon }}
          </v-icon>
        </v-btn>
      </slot>
    </div>

    <!-- Mobile dialog using v-dialog -->
    <v-dialog
      v-if="isMobile"
      v-model="isVisible"
      :max-width="maxWidth"
      :persistent="false"
      :overlay-opacity="0.5"
    >
      <v-card>
        <v-card-text :style="{ padding: padding }">
          <slot />
        </v-card-text>
        <v-card-actions class="justify-end pa-3">
          <v-btn
            text
            color="primary"
            @click="hide"
          >
            ปิด
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Desktop tooltip content will be created and appended to body via DOM manipulation -->
  </div>
</template>

<script>
export default {
  name: 'BaseRichTooltip',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    bottom: {
      type: Boolean,
      default: false
    },
    top: {
      type: Boolean,
      default: false
    },
    left: {
      type: Boolean,
      default: false
    },
    right: {
      type: Boolean,
      default: false
    },
    align: {
      type: String,
      default: 'center',
      validator: (value) => ['start', 'center', 'end'].includes(value)
    },
    maxWidth: {
      type: [String, Number],
      default: 400
    },
    maxHeight: {
      type: [String, Number],
      default: 500
    },
    disabled: {
      type: Boolean,
      default: false
    },
    openDelay: {
      type: Number,
      default: 0
    },
    closeDelay: {
      type: Number,
      default: 100
    },
    activatorIcon: {
      type: String,
      default: 'mdi-information-outline'
    },
    activatorColor: {
      type: String,
      default: 'primary'
    },
    iconSize: {
      type: [String, Number],
      default: 20
    },
    showArrow: {
      type: Boolean,
      default: true
    },
    arrowSize: {
      type: [String, Number],
      default: 8
    },
    arrowColor: {
      type: String,
      default: 'white'
    },
    arrowBorderColor: {
      type: String,
      default: '#e0e0e0'
    },
    background: {
      type: String,
      default: 'white'
    },
    border: {
      type: String,
      default: '1px solid #e0e0e0'
    },
    padding: {
      type: String,
      default: '16px'
    },
    trigger: {
      type: String,
      default: 'hover',
      validator: (value) => ['hover', 'click', 'manual'].includes(value)
    },
    target: {
      type: String,
      default: null
    },
    targetPosition: {
      type: String,
      default: null
    },
    disableMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isVisible: false,
      position: { top: 0, left: 0 },
      openTimer: null,
      closeTimer: null,
      tooltipId: `tooltip-${Math.random().toString(36).substr(2, 9)}`,
      tooltipElement: null,
      tooltipVue: null,
      arrowAdjustment: { x: 0, y: 0 },
      isMobile: false
    }
  },
  computed: {
    mainAxis() {
      if (this.top) return 'top'
      if (this.bottom) return 'bottom'
      if (this.left) return 'left'
      if (this.right) return 'right'
      return 'bottom'
    },
    tooltipClasses() {
      return [
        '_rich-tooltip',
        `_position-${this.mainAxis}`,
        `_align-${this.align}`
      ]
    },
    arrowClasses() {
      if (!this.showArrow) return ''
      return [
        '_has-arrow',
        `_arrow-${this.getOppositeAxis(this.mainAxis)}`,
        `_arrow-align-${this.align}`
      ]
    },
    tooltipStyles() {
      const styles = {
        'position': 'absolute',
        'top': `${this.position.top}px`,
        'left': `${this.position.left}px`,
        'zIndex': 2000,
        'pointerEvents': 'auto',
        '--arrow-size': `${this.arrowSize}px`,
        '--arrow-color': this.arrowColor,
        '--arrow-border-color': this.arrowBorderColor
      }

      if (this.maxWidth) {
        styles.maxWidth = typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : this.maxWidth
      }

      if (this.maxHeight) {
        styles.maxHeight = typeof this.maxHeight === 'number' ? `${this.maxHeight}px` : this.maxHeight
      }

      return styles
    },
    arrowPositionStyles() {
      if (!this.showArrow) return {}

      const arrowOffset = 20
      const arrowSize = parseInt(this.arrowSize, 10)
      const styles = {}
      const oppositeAxis = this.getOppositeAxis(this.mainAxis)

      // Position arrow outside the tooltip box with adjustments
      switch (oppositeAxis) {
        case 'top': // Arrow pointing up (for bottom tooltip)
          styles.top = `-${arrowSize}px`
          if (this.align === 'start') {
            styles.left = `${arrowOffset + this.arrowAdjustment.x}px`
            styles.transform = 'none'
          } else if (this.align === 'end') {
            styles.right = `${arrowOffset - this.arrowAdjustment.x}px`
            styles.left = 'auto'
            styles.transform = 'none'
          } else {
            styles.left = '50%'
            styles.transform = `translateX(calc(-50% + ${this.arrowAdjustment.x}px))`
          }
          break

        case 'bottom': // Arrow pointing down (for top tooltip)
          styles.bottom = `-${arrowSize}px`
          if (this.align === 'start') {
            styles.left = `${arrowOffset + this.arrowAdjustment.x}px`
            styles.transform = 'none'
          } else if (this.align === 'end') {
            styles.right = `${arrowOffset - this.arrowAdjustment.x}px`
            styles.left = 'auto'
            styles.transform = 'none'
          } else {
            styles.left = '50%'
            styles.transform = `translateX(calc(-50% + ${this.arrowAdjustment.x}px))`
          }
          break

        case 'left': // Arrow pointing left (for right tooltip)
          styles.left = `-${arrowSize}px`
          if (this.align === 'start') {
            styles.top = `${arrowOffset + this.arrowAdjustment.y}px`
            styles.transform = 'none'
          } else if (this.align === 'end') {
            styles.bottom = `${arrowOffset - this.arrowAdjustment.y}px`
            styles.top = 'auto'
            styles.transform = 'none'
          } else {
            styles.top = '50%'
            styles.transform = `translateY(calc(-50% + ${this.arrowAdjustment.y}px))`
          }
          break

        case 'right': // Arrow pointing right (for left tooltip)
          styles.right = `-${arrowSize}px`
          if (this.align === 'start') {
            styles.top = `${arrowOffset + this.arrowAdjustment.y}px`
            styles.transform = 'none'
          } else if (this.align === 'end') {
            styles.bottom = `${arrowOffset - this.arrowAdjustment.y}px`
            styles.top = 'auto'
            styles.transform = 'none'
          } else {
            styles.top = '50%'
            styles.transform = `translateY(calc(-50% + ${this.arrowAdjustment.y}px))`
          }
          break

        default:
          throw new Error(`Invalid oppositeAxis value: ${oppositeAxis}`)
      }

      return styles
    },
    shouldDisableTooltip() {
      return this.disableMobile && this.isMobile
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.show()
        } else {
          this.hide()
        }
      }
    },
    isVisible(newValue) {
      this.$emit('input', newValue)
    }
  },
  mounted() {
    this.detectMobile()
    this.setupTargetEventListeners()
    if (this.value) {
      this.show()
    }
  },
  beforeDestroy() {
    this.clearTimers()
    this.destroyTooltip()
    this.removeTargetEventListeners()
  },
  methods: {
    clearActiveElement() {
      // Clear active element to avoid focus issues on mobile
      if (this.isMobile) {
        document.activeElement.blur()
      }
    },
    onMouseEnter() {
      if (this.disabled || this.shouldDisableTooltip || this.trigger !== 'hover') return
      this.clearActiveElement()
      this.clearCloseTimer()
      this.openTimer = setTimeout(() => {
        this.calculatePositionAndShow()
      }, this.openDelay)
    },
    onActivatorMouseLeave() {
      if (this.disabled || this.trigger !== 'hover' || this.isMobile) return
      this.clearOpenTimer()
      this.scheduleClose()
    },
    onMouseLeave() {
      if (this.disabled || this.shouldDisableTooltip || this.trigger !== 'hover' || this.isMobile) return
      this.scheduleClose()
    },
    onTooltipMouseEnter() {
      if (this.disabled || this.trigger !== 'hover' || this.isMobile) return
      this.clearCloseTimer()
    },
    onTooltipMouseLeave() {
      if (this.disabled || this.trigger !== 'hover' || this.isMobile) return
      this.scheduleClose()
    },
    onClick() {
      if (this.disabled || this.shouldDisableTooltip || this.trigger !== 'click') return
      this.clearActiveElement()
      if (this.isVisible) {
        this.hide()
      } else {
        this.calculatePositionAndShow()
      }
    },

    show() {
      if (this.disabled || this.shouldDisableTooltip) return
      this.clearTimers()

      if (this.isMobile) {
        // For mobile, just show the v-dialog
        this.isVisible = true
      } else {
        // For desktop, create tooltip and position it
        this.createTooltip()
        this.isVisible = true
      }
    },
    calculatePositionAndShow() {
      if (this.disabled || this.shouldDisableTooltip) return
      this.clearTimers()

      if (this.isMobile) {
        // For mobile, just show the v-dialog
        this.isVisible = true
      } else {
        // Create tooltip and calculate position
        this.createTooltip()
        this.updatePosition()

        // Show tooltip after position is calculated
        this.isVisible = true

        // Fine-tune position after tooltip is rendered
        this.$nextTick(() => {
          this.updatePosition()
          this.updateArrowPosition()
        })
      }
    },
    hide() {
      this.clearTimers()
      this.isVisible = false
      this.arrowAdjustment = { x: 0, y: 0 }

      if (!this.isMobile) {
        this.destroyTooltip()
      }
    },
    toggle() {
      if (this.disabled || this.shouldDisableTooltip) return
      if (this.isVisible) {
        this.hide()
      } else {
        this.calculatePositionAndShow()
      }
    },
    scheduleClose() {
      this.closeTimer = setTimeout(() => {
        this.hide()
      }, this.closeDelay)
    },
    clearOpenTimer() {
      if (this.openTimer) {
        clearTimeout(this.openTimer)
        this.openTimer = null
      }
    },
    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer)
        this.closeTimer = null
      }
    },
    clearTimers() {
      this.clearOpenTimer()
      this.clearCloseTimer()
    },
    updatePosition() {
      if (!this.$refs.activator || !this.tooltipElement) return

      const positionTarget = this.getPositionTarget()
      const activatorRect = positionTarget.getBoundingClientRect()

      // Get actual viewport dimensions (accounts for dev tools)
      const viewport = {
        width: document.documentElement.clientWidth || window.innerWidth,
        height: document.documentElement.clientHeight || window.innerHeight,
        scrollX: window.pageXOffset || document.documentElement.scrollLeft,
        scrollY: window.pageYOffset || document.documentElement.scrollTop
      }

      // Get tooltip dimensions
      let tooltipRect = this.tooltipElement.getBoundingClientRect()
      if (tooltipRect.width === 0 || tooltipRect.height === 0) {
        // Estimate based on maxWidth/maxHeight if not visible yet
        tooltipRect = {
          width: this.maxWidth || 400,
          height: this.maxHeight || 200
        }
      }

      let top = 0
      let left = 0
      const gap = this.showArrow ? parseInt(this.arrowSize, 10) + 4 : 8
      const margin = 16

      // Calculate main axis position
      switch (this.mainAxis) {
        case 'top':
          top = activatorRect.top - tooltipRect.height - gap
          break
        case 'bottom':
          top = activatorRect.bottom + gap
          break
        case 'left':
          left = activatorRect.left - tooltipRect.width - gap
          break
        case 'right':
          left = activatorRect.right + gap
          break
        default:
          throw new Error(`Invalid mainAxis value: ${this.mainAxis}`)
      }

      // Calculate cross axis position
      if (this.mainAxis === 'top' || this.mainAxis === 'bottom') {
        switch (this.align) {
          case 'start':
            left = activatorRect.left
            break
          case 'center':
            left = activatorRect.left + (activatorRect.width / 2) - (tooltipRect.width / 2)
            break
          case 'end':
            left = activatorRect.right - tooltipRect.width
            break
          default:
            throw new Error(`Invalid align value: ${this.align}`)
        }
      } else if (this.mainAxis === 'left' || this.mainAxis === 'right') {
        switch (this.align) {
          case 'start':
            top = activatorRect.top
            break
          case 'center':
            top = activatorRect.top + (activatorRect.height / 2) - (tooltipRect.height / 2)
            break
          case 'end':
            top = activatorRect.bottom - tooltipRect.height
            break
          default:
            throw new Error(`Invalid align value: ${this.align}`)
        }
      }

      // Viewport boundary checks
      const minX = margin
      const maxX = viewport.width - tooltipRect.width - margin
      const minY = margin
      const maxY = viewport.height - tooltipRect.height - margin

      if (left < minX) {
        left = minX
      } else if (left > maxX) {
        left = maxX
      }

      if (top < minY) {
        top = minY
      } else if (top > maxY) {
        top = maxY
      }

      // Apply position with scroll offset
      const finalTop = top + viewport.scrollY
      const finalLeft = left + viewport.scrollX

      this.tooltipElement.style.top = `${finalTop}px`
      this.tooltipElement.style.left = `${finalLeft}px`

      this.position = { top: finalTop, left: finalLeft }
    },
    createTooltip() {
      if (this.tooltipElement) return

      // Create tooltip element
      this.tooltipElement = document.createElement('div')
      this.tooltipElement.id = this.tooltipId
      this.tooltipElement.className = `_rich-tooltip-container ${this.tooltipClasses.join(' ')} ${this.arrowClasses.join(' ')}`

      // Apply styles
      Object.assign(this.tooltipElement.style, {
        'position': 'absolute',
        'zIndex': '2000',
        'pointerEvents': 'auto',
        'opacity': '0',
        'transform': 'scale(0.95)',
        'transition': 'all 0.2s ease',
        'background': this.background,
        'border': this.border,
        'borderRadius': '8px',
        'boxShadow': '0 4px 12px rgba(0, 0, 0, 0.15)',
        '--arrow-size': `${this.arrowSize}px`,
        '--arrow-color': this.arrowColor,
        '--arrow-border-color': this.arrowBorderColor
      })

      if (this.maxWidth) {
        this.tooltipElement.style.maxWidth = typeof this.maxWidth === 'number' ? `${this.maxWidth}px` : this.maxWidth
      }
      if (this.maxHeight) {
        this.tooltipElement.style.maxHeight = typeof this.maxHeight === 'number' ? `${this.maxHeight}px` : this.maxHeight
      }

      // Create arrow if needed
      if (this.showArrow) {
        const arrow = document.createElement('div')
        arrow.className = '_tooltip-arrow'

        // Apply base arrow styles
        Object.assign(arrow.style, {
          position: 'absolute',
          width: '0',
          height: '0',
          zIndex: '1'
        })

        // Apply arrow direction styles
        this.applyArrowStyles(arrow)

        // Apply initial positioning styles (will be updated after positioning)
        Object.assign(arrow.style, this.arrowPositionStyles)

        this.tooltipElement.appendChild(arrow)
      }

      // Create a temporary Vue instance to render the slot content
      const TooltipContent = {
        render: (h) => h('div', {
          class: '_rich-tooltip-content',
          style: {
            padding: this.padding
          }
        }, this.$slots.default)
      }

      this.tooltipVue = new this.$root.constructor({
        ...TooltipContent,
        parent: this
      }).$mount()

      this.tooltipElement.appendChild(this.tooltipVue.$el)

      // Add event listeners
      this.tooltipElement.addEventListener('mouseenter', this.onTooltipMouseEnter)
      this.tooltipElement.addEventListener('mouseleave', this.onTooltipMouseLeave)

      // Append to body
      document.body.appendChild(this.tooltipElement)

      // Trigger animation
      this.$nextTick(() => {
        if (this.tooltipElement) {
          this.tooltipElement.style.opacity = '1'
          this.tooltipElement.style.transform = 'scale(1)'
        }
      })
    },
    destroyTooltip() {
      if (this.tooltipElement) {
        // Fade out animation
        this.tooltipElement.style.opacity = '0'
        this.tooltipElement.style.transform = 'scale(0.95)'

        setTimeout(() => {
          if (this.tooltipElement && this.tooltipElement.parentNode) {
            this.tooltipElement.removeEventListener('mouseenter', this.onTooltipMouseEnter)
            this.tooltipElement.removeEventListener('mouseleave', this.onTooltipMouseLeave)
            this.tooltipElement.parentNode.removeChild(this.tooltipElement)
          }
          if (this.tooltipVue) {
            this.tooltipVue.$destroy()
            this.tooltipVue = null
          }
          this.tooltipElement = null
        }, 200)
      }
    },
    applyArrowStyles(el) {
      const size = parseInt(this.arrowSize, 10)
      const oppositeAxis = this.getOppositeAxis(this.mainAxis)

      // Use tooltip's background and border colors for arrow
      const arrowBg = this.background
      const arrowBorder = this.border.includes('solid') ? this.border.split('solid')[1].trim() : this.arrowBorderColor

      // Reset all border styles
      el.style.borderTop = 'none'
      el.style.borderBottom = 'none'
      el.style.borderLeft = 'none'
      el.style.borderRight = 'none'

      // Apply direction-specific styles
      switch (oppositeAxis) {
        case 'top': // Arrow pointing up (for bottom tooltip)
          el.style.borderLeft = `${size}px solid transparent`
          el.style.borderRight = `${size}px solid transparent`
          el.style.borderBottom = `${size}px solid ${arrowBorder}`

          // Create inner arrow with tooltip background
          el.innerHTML = `<div style="
            position: absolute;
            top: 1px;
            left: -${size - 1}px;
            width: 0;
            height: 0;
            border-left: ${size - 1}px solid transparent;
            border-right: ${size - 1}px solid transparent;
            border-bottom: ${size - 1}px solid ${arrowBg};
          "></div>`
          break

        case 'bottom': // Arrow pointing down (for top tooltip)
          el.style.borderLeft = `${size}px solid transparent`
          el.style.borderRight = `${size}px solid transparent`
          el.style.borderTop = `${size}px solid ${arrowBorder}`

          el.innerHTML = `<div style="
            position: absolute;
            bottom: 1px;
            left: -${size - 1}px;
            width: 0;
            height: 0;
            border-left: ${size - 1}px solid transparent;
            border-right: ${size - 1}px solid transparent;
            border-top: ${size - 1}px solid ${arrowBg};
          "></div>`
          break

        case 'left': // Arrow pointing left (for right tooltip)
          el.style.borderTop = `${size}px solid transparent`
          el.style.borderBottom = `${size}px solid transparent`
          el.style.borderRight = `${size}px solid ${arrowBorder}`

          el.innerHTML = `<div style="
            position: absolute;
            left: 1px;
            top: -${size - 1}px;
            width: 0;
            height: 0;
            border-top: ${size - 1}px solid transparent;
            border-bottom: ${size - 1}px solid transparent;
            border-right: ${size - 1}px solid ${arrowBg};
          "></div>`
          break

        case 'right': // Arrow pointing right (for left tooltip)
          el.style.borderTop = `${size}px solid transparent`
          el.style.borderBottom = `${size}px solid transparent`
          el.style.borderLeft = `${size}px solid ${arrowBorder}`

          el.innerHTML = `<div style="
            position: absolute;
            right: 1px;
            top: -${size - 1}px;
            width: 0;
            height: 0;
            border-top: ${size - 1}px solid transparent;
            border-bottom: ${size - 1}px solid transparent;
            border-left: ${size - 1}px solid ${arrowBg};
          "></div>`
          break

        default:
          throw new Error(`Invalid oppositeAxis value: ${oppositeAxis}`)
      }
    },
    getOppositeAxis(axis) {
      const opposites = {
        top: 'bottom',
        bottom: 'top',
        left: 'right',
        right: 'left'
      }
      return opposites[axis] || 'bottom'
    },
    updateArrowPosition() {
      if (!this.showArrow || !this.$refs.activator || !this.tooltipElement) {
        return
      }

      const positionTarget = this.getPositionTarget()
      const activatorRect = positionTarget.getBoundingClientRect()
      const tooltipRect = this.tooltipElement.getBoundingClientRect()
      const arrowOffset = 20
      const minGap = 4

      // Calculate arrow adjustment based on current positioning and alignment
      if (this.mainAxis === 'top' || this.mainAxis === 'bottom') {
        // For horizontal tooltips, adjust arrow X position
        const activatorCenter = activatorRect.left + (activatorRect.width / 2)

        let intendedArrowPosition
        if (this.align === 'start') {
          intendedArrowPosition = tooltipRect.left + arrowOffset
        } else if (this.align === 'end') {
          intendedArrowPosition = tooltipRect.right - arrowOffset
        } else {
          // center alignment
          intendedArrowPosition = tooltipRect.left + (tooltipRect.width / 2)
        }

        // Calculate adjustment with minimum gap constraints
        let adjustment = activatorCenter - intendedArrowPosition
        const minX = tooltipRect.left + minGap
        const maxX = tooltipRect.right - minGap
        const finalArrowPosition = intendedArrowPosition + adjustment

        if (finalArrowPosition < minX) {
          adjustment = minX - intendedArrowPosition
        } else if (finalArrowPosition > maxX) {
          adjustment = maxX - intendedArrowPosition
        }

        this.arrowAdjustment.x = adjustment
      } else if (this.mainAxis === 'left' || this.mainAxis === 'right') {
        // For vertical tooltips, adjust arrow Y position
        const activatorCenter = activatorRect.top + (activatorRect.height / 2)

        let intendedArrowPosition
        if (this.align === 'start') {
          intendedArrowPosition = tooltipRect.top + arrowOffset
        } else if (this.align === 'end') {
          intendedArrowPosition = tooltipRect.bottom - arrowOffset
        } else {
          // center alignment
          intendedArrowPosition = tooltipRect.top + (tooltipRect.height / 2)
        }

        // Calculate adjustment with minimum gap constraints
        let adjustment = activatorCenter - intendedArrowPosition
        const minY = tooltipRect.top + minGap
        const maxY = tooltipRect.bottom - minGap
        const finalArrowPosition = intendedArrowPosition + adjustment

        if (finalArrowPosition < minY) {
          adjustment = minY - intendedArrowPosition
        } else if (finalArrowPosition > maxY) {
          adjustment = maxY - intendedArrowPosition
        }

        this.arrowAdjustment.y = adjustment
      }

      // Update arrow styles
      const arrow = this.tooltipElement.querySelector('._tooltip-arrow')
      if (arrow) {
        Object.assign(arrow.style, this.arrowPositionStyles)
      }
    },
    getEventTarget() {
      if (this.target) {
        const targetElement = this.$el.querySelector(this.target)
        return targetElement
      }
      return null
    },
    getPositionTarget() {
      if (this.targetPosition) {
        const targetElement = this.$el.querySelector(this.targetPosition)
        return targetElement || this.$refs.activator
      }
      if (this.target) {
        const targetElement = this.$el.querySelector(this.target)
        return targetElement || this.$refs.activator
      }
      return this.$refs.activator
    },
    setupTargetEventListeners() {
      const eventTarget = this.getEventTarget()

      if (eventTarget) {
        if (this.trigger === 'hover') {
          eventTarget.addEventListener('mouseenter', this.onMouseEnter)
          eventTarget.addEventListener('mouseleave', this.onActivatorMouseLeave)
        } else if (this.trigger === 'click') {
          eventTarget.addEventListener('click', this.onClick)
        }
      }
    },
    removeTargetEventListeners() {
      const eventTarget = this.getEventTarget()

      if (eventTarget) {
        if (this.trigger === 'hover') {
          eventTarget.removeEventListener('mouseenter', this.onMouseEnter)
          eventTarget.removeEventListener('mouseleave', this.onActivatorMouseLeave)
        } else if (this.trigger === 'click') {
          eventTarget.removeEventListener('click', this.onClick)
        }
      }
    },
    detectMobile() {
      // Check for mobile devices using multiple methods
      this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
                     || (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
                     || window.innerWidth <= 768
    }
  }
}
</script>

<style lang="scss" scoped>
._rich-tooltip-wrapper {
  display: inline-block;
}

._tooltip-activator {
  cursor: pointer;
}

._rich-tooltip-container {
  min-width: 200px;
  max-width: 400px;
  overflow: visible;
}

._rich-tooltip-content {
  overflow-y: auto;
  max-height: inherit;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
}

/* Arrow styles are now handled via JavaScript DOM manipulation */

/* Transition */
.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: all 0.2s ease;
}

.tooltip-fade-enter,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
